import { useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { User, Job } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { actionLinkClassName } from '@core/theme/actionTheme.util';
import CvProfileDisplay from '@/components/shared/CvProfileDisplay';
import { getWorkerCvSummary } from '@/modules/workers/utils/cvProfile.utils';
import { isFeatureEnabled } from '@/utils/featureFlags';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';

interface WorkerApplicationProps {
  worker: User;
  job: Job;
  coverLetter?: string | null;
  onAssignWorker: (worker: User) => void;
}

const WorkerApplication = ({
  worker,
  job,
  coverLetter,
  onAssignWorker,
}: WorkerApplicationProps) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const jobInPast = job?.startDate
    ? dayjs(parseInt(job.startDate, 10)).isBefore(dayjs())
    : true;
  const { getTerm } = useTemplateStore();
  const hasCandidateSummary = Boolean(getWorkerCvSummary(worker));
  const displayCandidateSuburbForClients = useCoreLoopConfigurationStore(
    (state) =>
      state.coreLoopConfigurationItems?.displayCandidateSuburbForClients ===
      true,
  );
  const applicantLocation = [worker?.address?.suburb, worker?.address?.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex w-full flex-col rounded-md bg-white">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center overflow-hidden rounded-full bg-gray-100">
            {worker?.profilePictureUrl ? (
              <img
                src={worker.profilePictureUrl}
                alt={worker?.firstName + ' ' + worker?.lastName}
                className="h-10 w-10 object-contain"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-200" />
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-sm font-medium text-gray-900">{`${worker?.firstName} ${worker?.lastName}`}</p>
            {displayCandidateSuburbForClients && applicantLocation ? (
              <p className="flex items-center gap-1.5 text-sm text-gray-400">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  title="Applicant location"
                  className="h-3 w-3"
                />
                {applicantLocation}
              </p>
            ) : null}
            <p className="text-sm text-gray-400">
              {job?.classification?.name || 'Job classification not set'}
            </p>
          </div>
        </div>
        <button
          className={`cursor-pointer rounded-lg bg-blue-600 px-2 py-1 text-white ${jobInPast ? 'cursor-not-allowed opacity-50' : ''} hover:bg-blue-500`}
          type="button"
          title={
            !worker
              ? `No ${getTerm(TemplateTerm.WORKER, false)} selected`
              : `Assign ${getTerm(TemplateTerm.WORKER, false)}`
          }
          onClick={() => {
            onAssignWorker(worker);
          }}
        >
          Assign
        </button>
      </div>
      {coverLetter?.trim() ? (
        <blockquote className="mx-3 mb-3 text-sm break-words text-gray-700 italic">
          “{coverLetter.trim()}”
        </blockquote>
      ) : null}

      {hasCandidateSummary && isFeatureEnabled('candidateSummary') ? (
        <>
          {isSummaryExpanded ? (
            <div className="border-t border-gray-200 px-3 py-3">
              <CvProfileDisplay worker={worker} />
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            className={`flex cursor-pointer items-center justify-center gap-2 border-t border-gray-200 py-2 text-sm font-medium ${actionLinkClassName}`}
          >
            {isSummaryExpanded ? 'Close' : 'Candidate Summary'}
            <FontAwesomeIcon
              icon={isSummaryExpanded ? faChevronUp : faChevronDown}
              className="h-3 w-3"
            />
          </button>
        </>
      ) : null}
    </div>
  );
};

export default WorkerApplication;
